import React, { MouseEvent, ReactElement } from 'react';
import MuiListItemButton, {
  ListItemButtonTypeMap,
  ListItemButtonProps as MuiListItemButtonProps
} from '@mui/material/ListItemButton';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { BpDesignSystem } from '../../types';
import { styled, css } from '../../styles';
import { mergeClassNames } from '../../utils';

export type ListItemButtonProps<
  D extends React.ElementType = ListItemButtonTypeMap['defaultComponent'],
  P = {}
> = BpDesignSystem.PropsWithChildren<
  MuiListItemButtonProps<D, P> & {
    disabled?: boolean;
    selected?: boolean;
    wrapperDataTest?: string;
    hasEndIcon?: boolean;
    tabIndex?: number;
  }
>;

// This is ex MenuListItem component
export const ListItemButton = <D extends React.ElementType = ListItemButtonTypeMap['defaultComponent'], P = {}>({
  children,
  disabled,
  style,
  selected,
  className,
  wrapperDataTest,
  hasEndIcon,
  tabIndex,
  dataTest,
  component,
  ...rest
}: ListItemButtonProps<D, P>): ReactElement => {
  const clickHandler = (e: MouseEvent<HTMLElement>) => {
    if (disabled) {
      e.stopPropagation();
      e.preventDefault();
    } else {
      (rest as ListItemButtonProps<'button', {}>).onClick?.(e as MouseEvent<HTMLButtonElement>);
    }
  };
  return (
    <StyledListItemButton
      data-test={dataTest}
      disableRipple
      onClick={clickHandler}
      disabled={disabled}
      tabIndex={tabIndex}
      component="button"
      $hasEndIcon={hasEndIcon}
      className={mergeClassNames(className ?? '', {
        disabled,
        selected
      })}
      selected={selected}
      style={{ color: tokens.colorOnSurface, ...style }}
      {...rest}
    >
      {children}
    </StyledListItemButton>
  );
};

const StyledListItemButton = styled(MuiListItemButton)<{ $hasEndIcon?: boolean }>`
  ${({ $hasEndIcon }) => ($hasEndIcon ? withEndIconCss : undefined)}
`;

const withEndIconCss = css`
  && {
    justify-content: space-between;

    .MuiListItemIcon-root {
      justify-content: flex-end;
      margin-left: 8px;
    }
  }
`;
